import { useState } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, BookOpenText, Tags, FileBarChart, Building2, MessageCircle, ShieldCheck, LogOut, Menu, X, Users,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBusiness } from "../context/BusinessContext";
import { LicenseBanner } from "./LicenseBanner";
import { AIAssistant } from "./AIAssistant";
import { LicenseExpired } from "./LicenseExpired";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const NAV = [
  { to: "/", label: "Ana Sayfa", icon: LayoutDashboard, id: "dashboard" },
  { to: "/defter", label: "Günlük Defter", icon: BookOpenText, id: "daily-ledger" },
  { to: "/kategoriler", label: "Kategoriler", icon: Tags, id: "categories" },
  { to: "/personel", label: "Personel", icon: Users, id: "staff" },
  { to: "/raporlar", label: "Raporlar", icon: FileBarChart, id: "reports" },
  { to: "/isletmeler", label: "İşletmeler", icon: Building2, id: "businesses" },
  { to: "/iletisim", label: "İletişim", icon: MessageCircle, id: "contact" },
];

function NavItems({ onClick, isAdmin }) {
  const items = isAdmin ? [...NAV, { to: "/admin", label: "SüperAdmin", icon: ShieldCheck, id: "superadmin-menu" }] : NAV;
  return (
    <nav className="space-y-1">
      {items.map(({ to, label, icon: Icon, id }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          onClick={onClick}
          data-testid={id === "superadmin-menu" ? "superadmin-menu-link" : `${id}-tab`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
              isActive ? "bg-gradient-to-r from-cyan-600 to-blue-800 text-white shadow-md shadow-cyan-900/20" : "text-slate-600 hover:bg-slate-100"
            }`
          }
        >
          <Icon className="h-[18px] w-[18px]" /> {label}
        </NavLink>
      ))}
    </nav>
  );
}

function BusinessSwitcher() {
  const { businesses, current, setCurrentId } = useBusiness();
  if (!businesses.length) return null;
  return (
    <Select value={current?.id} onValueChange={setCurrentId}>
      <SelectTrigger data-testid="business-switcher-select" className="h-10 w-full max-w-[260px] rounded-xl border-slate-200 bg-white">
        <Building2 className="mr-2 h-4 w-4 text-cyan-700" />
        <SelectValue placeholder="İşletme seçin" />
      </SelectTrigger>
      <SelectContent>
        {businesses.map((b) => (
          <SelectItem key={b.id} value={b.id} data-testid={`business-option-${b.id}`}>
            {b.name} <span className="text-xs text-slate-400">· {b.kind === "sahis" ? "Şahıs" : "İşletme"}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();
  const isAdmin = user.role === "superadmin";
  const blocked = !isAdmin && !user.license_active && loc.pathname !== "/iletisim";

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-slate-200 bg-white p-4 lg:flex">
        <Link to="/" data-testid="nav-logo" className="mb-6 block">
          <img src="/img/kasamnet-logo.jpg" alt="Kasam Net" className="mx-auto h-28 w-auto object-contain mix-blend-multiply" />
        </Link>
        <NavItems isAdmin={isAdmin} />
        <div className="mt-auto rounded-2xl bg-slate-50 p-3">
          <div className="text-sm font-semibold text-slate-800" data-testid="current-user-name">{user.first_name} {user.last_name}</div>
          <div className="text-xs text-slate-500">@{user.username}</div>
          <button data-testid="logout-button" onClick={logout} className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white py-2 text-sm text-slate-600 hover:bg-slate-100">
            <LogOut className="h-4 w-4" /> Çıkış Yap
          </button>
        </div>
      </aside>

      {mobile && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 lg:hidden" onClick={() => setMobile(false)}>
          <div className="h-full w-72 bg-white p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <img src="/img/kasamnet-logo.jpg" alt="Kasam Net" className="h-16 mix-blend-multiply" />
              <button data-testid="mobile-menu-close" onClick={() => setMobile(false)}><X className="h-5 w-5" /></button>
            </div>
            <NavItems isAdmin={isAdmin} onClick={() => setMobile(false)} />
            <button onClick={logout} data-testid="mobile-logout-button" className="mt-6 flex items-center gap-2 px-3 text-sm text-slate-600">
              <LogOut className="h-4 w-4" /> Çıkış Yap
            </button>
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur-md sm:px-6 lg:px-8">
          <button data-testid="mobile-menu-button" className="lg:hidden" onClick={() => setMobile(true)}>
            <Menu className="h-6 w-6 text-slate-700" />
          </button>
          <BusinessSwitcher />
          <div className="ml-auto hidden text-right text-xs text-slate-500 sm:block">
            Lisans bitişi
            <div className="font-semibold text-slate-800" data-testid="license-end-date">
              {new Date(user.license_end).toLocaleDateString("tr-TR")}
            </div>
          </div>
        </header>
        <main className="space-y-6 p-4 pb-28 sm:p-6 lg:p-8">
          <LicenseBanner />
          {blocked ? <LicenseExpired /> : <Outlet />}
        </main>
      </div>
      {!blocked && <AIAssistant />}
    </div>
  );
}
